import React from 'react';
import { useCategoryMeta, type RowLayout } from '../context/ClassificationSchemaContext';
import { HANDLING_ICONS, type HandlingIconName } from './icons';

// The BOX paint of a row's primary-axis colour band (part 7 fix loop, 2026-10-12): the served hue, or
// -- for the unclassified value -- the slate tint (fill #e6e7ec, no visible rim), via the one rule in
// categoryMeta.resolvePrimarySwatch. Every band keys its width explicitly, so border-box keeps the
// border (the tint form: its own fill colour, no visible rim) inside it. No axis yet: the caller's neutral fallback, unbordered.
// `open` (small facts on a row, operator pick C, 2026-10-19): on the OPEN list row the band's border
// is the swatch's INK (`openBorder`), so the strip's box reappears as a thin frame — slate on the
// unclassified value, white on a hue — where the row's grey selection fill would otherwise swallow
// it. Same width, so nothing moves; the border colour alone transitions.
export const primaryBandStyle = (
  cm: Pick<ReturnType<typeof useCategoryMeta>, 'swatch'>,
  value: string | null | undefined,
  fallback: string,
  open = false,
): React.CSSProperties => {
  if (!value) return { backgroundColor: fallback };
  const sw = cm.swatch(value);
  return { backgroundColor: sw.fill, border: open ? sw.openBorder : sw.border, boxSizing: 'border-box', transition: 'border-color 0.15s ease' };
};

// ─── HandlingStripFace + handlingTooltipText ────────────
// <purpose>THE face inside the row's colour strip, and THE text both vocabulary hovers read (wim
// vocabulary v7, 2026-09-13). Three lists render that strip — the active list (AntwortenSeite), the
// ledger (ArchivSeite) and the dashboard's Verlauf (DashboardSeite) — and each used to inline its
// own abbr span. With v7 the face gained a tier order (served glyph → served Kürzel → nothing), and
// a tier order copied three times is three places for it to drift, so it lives here once.</purpose>
// <constraints>The strip's BOX is the call site's — width, flexShrink, background hue, centring all
// stay there, untouched. This component only fills it, which is what keeps the layout law intact:
// no glyph, a Kürzel, or an empty strip are all the same column geometry, so a vocabulary landing
// (or a mode flip) can never re-flow a row. Renders NOTHING for strip_label (line 2's badge speaks
// there) and nothing for a row with no axis yet — an absence, never a placeholder.</constraints>
export const HandlingStripFace: React.FC<{ value: string | null; layout: RowLayout; size?: number }> = ({ value, layout, size = 16 }) => {
  const cm = useCategoryMeta();
  if (!value || layout !== 'strip_abbr') return null;
  const served = cm.icon(value);
  // A served name the bundle has no glyph for is treated exactly like no icon at all: the Kürzel is
  // the honest face, and drawing a stand-in box would assert a meaning nobody served.
  const Glyph = served ? HANDLING_ICONS[served as HandlingIconName] as React.FC<{ size?: number; color?: string }> | undefined : undefined;
  // The ink follows the band: white on a served hue, slate on the tinted unclassified band.
  const ink = cm.swatch(value).ink;
  if (Glyph) return <Glyph size={size} color={ink} />;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, color: ink }}>
      {cm.abbr(value)}
    </span>
  );
};

// The hover text for a classified value, wherever it is shown: the tenant's word, plus the served
// sentence saying what it MEANS when there is one. ONE helper because the strip and the line-2 badge
// name the same value on the same row — two hand-written templates would be two chances to word it
// differently, and the glyph face makes the hint load-bearing rather than decorative.
// A plain colon joins the halves: the product emits no decorative Unicode, so no dash and no arrow.
// A value that serves no hint hovers its label alone — never an empty bubble, never an invented
// sentence (see resolveHint's own single-tier reasoning).
export function handlingTooltipText(
  cm: Pick<ReturnType<typeof useCategoryMeta>, 'label' | 'hint'>,
  value: string,
): string {
  const label = cm.label(value);
  const hint = cm.hint(value);
  return hint ? `${label}: ${hint}` : label;
}

// THE "is this bubble worth a tab stop?" predicate (final review of v7, 2026-09-13) — the companion
// of handlingTooltipText, here for the same reason: the badge surfaces each decide whether to wrap,
// and three copies of the condition would be three places for it to drift.
// A tooltip host is not free. InfoTooltip gives a non-focusable anchor tabIndex=0 so the text is
// keyboard-reachable, which is right when the bubble carries a FACT and wrong when it repeats the
// word already on screen — one dead tab stop per badge, per row. So: wrap when the value serves a
// hint (the sentence saying what it MEANS), or when the visible FACE is not the label (a compact id
// face, where the bubble is the only place the German word appears). Both false ⇒ the badge already
// says everything the bubble would.
// The strip hovers do NOT consult this: a strip renders a glyph or a Kürzel and never the label, so
// its face is never the label and there is always something to say.
export function hasSomethingToSay(
  cm: Pick<ReturnType<typeof useCategoryMeta>, 'label' | 'hint'>,
  value: string,
  face: string,
): boolean {
  return cm.hint(value) !== null || face !== cm.label(value);
}
